<template>
  <el-dialog
    title="每天定时清理规则"
    :visible.sync="visible"
    width="640px"
    append-to-body
    :close-on-click-modal="false"
    custom-class="schedule-rule-dialog"
    @open="onOpen"
  >
    <p class="schedule-hint">
      规则对勾选店铺生效：建议下架与必须下架都会清理。近 N 天更新过的商品会跳过（安全保护）。可设置多个时段，每个时段每天各执行一次。
    </p>
    <el-form :model="scheduleForm" label-width="110px" size="small" class="schedule-form">
      <el-form-item label="启用定时">
        <el-switch v-model="scheduleForm.enabled" :active-value="1" :inactive-value="0" />
        <span class="form-tip">需保持客户端已登录运行</span>
      </el-form-item>
      <el-form-item label="每天运行时间" required>
        <div class="time-slots">
          <div
            v-for="(hm, idx) in scheduleForm.run_hms"
            :key="'slot_' + idx"
            class="time-slot-row"
          >
            <el-time-picker
              :value="hm"
              placeholder="选择时刻"
              format="HH:mm"
              value-format="HH:mm"
              :clearable="false"
              @input="(val) => onSlotChange(idx, val)"
            />
            <el-button
              type="text"
              class="slot-remove"
              :disabled="scheduleForm.run_hms.length <= 1"
              @click="removeSlot(idx)"
            >
              删除
            </el-button>
          </div>
          <el-button
            type="text"
            icon="el-icon-plus"
            :disabled="scheduleForm.run_hms.length >= maxSlots"
            @click="addSlot"
          >
            添加时段
          </el-button>
          <div class="form-tip slot-tip">最多 {{ maxSlots }} 个时段；到点后客户端在线会自动执行</div>
        </div>
      </el-form-item>
      <el-form-item label="处理方式" required>
        <el-radio-group v-model="scheduleForm.op_type">
          <el-radio label="offline">下架处理</el-radio>
          <el-radio label="delete">删除商品</el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item label="执行店铺" required>
        <ShopGroupMultiSelect
          v-model="scheduleForm.shop_ids"
          :shops="shopList"
          size="small"
          group-width="140px"
          shop-width="calc(100% - 148px)"
          placeholder="选择要按规则清理的抖店（可多选）"
          :wrap-style="{ display: 'flex', width: '100%' }"
        />
        <div class="shop-actions">
          <el-button type="text" @click="selectAllShops">全选</el-button>
          <el-button type="text" @click="scheduleForm.shop_ids = []">清空</el-button>
        </div>
      </el-form-item>
      <el-form-item label="安全保护">
        <div class="protect-row">
          <span>近</span>
          <el-input-number
            v-model="scheduleForm.protect_days"
            :min="0"
            :max="7"
            controls-position="right"
            class="protect-input"
          />
          <span>天更新过的商品不处理；填 0 表示关闭保护</span>
        </div>
        <div class="form-tip protect-tip">填 0 表示不保护；最大 7 天。刚更新过的商品可能还在观察期。</div>
      </el-form-item>
      <el-form-item label="上次执行">
        <div v-if="scheduleForm.last_run_at || scheduleForm.last_run_msg" class="last-run-box">
          <div v-if="scheduleForm.last_run_at" class="last-run">{{ formatScheduleLastRun }}</div>
          <div
            v-if="scheduleForm.last_run_msg"
            class="last-run-msg"
            :class="{
              'is-error': /授权|未登录|异常/.test(scheduleForm.last_run_msg),
              'is-warning': /跳过|失败|手动清理进行中/.test(scheduleForm.last_run_msg),
            }"
          >
            {{ scheduleForm.last_run_msg }}
          </div>
        </div>
        <span v-else class="f-disable">暂无</span>
      </el-form-item>
    </el-form>
    <div slot="footer" class="dialog-footer">
      <el-button size="small" :loading="loading" @click="loadConfig">重新加载</el-button>
      <el-button size="small" @click="visible = false">取消</el-button>
      <el-button type="primary" size="small" :loading="saving" @click="saveConfig">保存规则</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { fetchDouDianAccounts } from '@/script/doudianLocalLogin';
import ShopGroupMultiSelect from '@/components/ShopGroupMultiSelect.vue';

const MAX_SLOTS = 8;

const defaultForm = () => ({
  enabled: 0,
  run_hm: "05:30",
  run_hms: ["05:30"],
  op_type: "offline",
  protect_days: 0,
  shop_ids: [],
  last_run_day: "",
  last_run_slots: [],
  last_run_at: 0,
  last_run_msg: "",
});

function normalizeHm(hm) {
  const s = String(hm || "").trim();
  if (!/^\d{1,2}:\d{2}$/.test(s)) return "";
  const [h, m] = s.split(":").map((n) => parseInt(n, 10));
  const hh = Math.min(23, Math.max(0, h || 0));
  const mm = Math.min(59, Math.max(0, m || 0));
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

function normalizeRunHms(list, fallback) {
  const src = Array.isArray(list) && list.length ? list : fallback ? [fallback] : ["05:30"];
  const seen = new Set();
  const out = [];
  src.forEach((hm) => {
    const v = normalizeHm(hm);
    if (!v || seen.has(v)) return;
    seen.add(v);
    out.push(v);
  });
  if (!out.length) out.push("05:30");
  out.sort();
  return out.slice(0, MAX_SLOTS);
}

export default {
  name: "LowQualityScheduleDialog",
  components: { ShopGroupMultiSelect },
  props: {
    value: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: false,
      saving: false,
      shopList: [],
      maxSlots: MAX_SLOTS,
      scheduleForm: defaultForm(),
    };
  },
  computed: {
    visible: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
    formatScheduleLastRun() {
      const t = this.scheduleForm.last_run_at;
      if (!t) return "";
      if (this.$utils && this.$utils.formatDateHHmmss) {
        return this.$utils.formatDateHHmmss(t);
      }
      return String(t);
    },
    summary() {
      const runHms = normalizeRunHms(this.scheduleForm.run_hms, this.scheduleForm.run_hm);
      return {
        enabled: this.scheduleForm.enabled,
        run_hm: runHms[0] || "05:30",
        run_hms: runHms,
        op_type: this.scheduleForm.op_type,
        shop_count: (this.scheduleForm.shop_ids || []).length,
        shop_ids: (this.scheduleForm.shop_ids || []).map(String),
        last_run_at: this.scheduleForm.last_run_at || 0,
        last_run_msg: this.scheduleForm.last_run_msg || "",
      };
    },
  },
  methods: {
    open() {
      this.visible = true;
    },
    async onOpen() {
      await Promise.all([this.loadShops(), this.loadConfig()]);
    },
    async loadShops() {
      try {
        const res = await fetchDouDianAccounts((url, data) => this.$api.post(url, data), {
          requireLogin: true,
          status: 1,
          pageSize: 1000,
        });
        if (res.code == 1) {
          this.shopList =
            res.list?.filter((it) => it.source_type == 1).map((it) => ({
              ...it,
              id: String(it.id),
            })) || [];
        }
      } catch (e) {
        /* ignore */
      }
    },
    async loadConfig() {
      this.loading = true;
      try {
        const res = await this.$call.invoke("SqliteDB.excute", {
          action: "getLowQualityScheduleConfig",
        });
        if (res.code == 1 && res.data) {
          const runHms = normalizeRunHms(res.data.run_hms, res.data.run_hm || "05:30");
          this.scheduleForm = {
            enabled: res.data.enabled == 1 ? 1 : 0,
            run_hm: runHms[0] || "05:30",
            run_hms: runHms,
            op_type: res.data.op_type === "delete" ? "delete" : "offline",
            protect_days:
              res.data.protect_days != null ? Number(res.data.protect_days) : 0,
            shop_ids: (res.data.shop_ids || []).map(String),
            last_run_day: res.data.last_run_day || "",
            last_run_slots: Array.isArray(res.data.last_run_slots)
              ? res.data.last_run_slots
              : [],
            last_run_at: res.data.last_run_at || 0,
            last_run_msg: res.data.last_run_msg || "",
          };
          this.$emit("loaded", this.summary);
        }
      } catch (e) {
        /* ignore */
      }
      this.loading = false;
    },
    onSlotChange(idx, val) {
      const next = [...this.scheduleForm.run_hms];
      next[idx] = normalizeHm(val) || next[idx] || "05:30";
      this.scheduleForm.run_hms = next;
      this.scheduleForm.run_hm = next[0] || "05:30";
    },
    addSlot() {
      if (this.scheduleForm.run_hms.length >= MAX_SLOTS) return;
      this.scheduleForm.run_hms = [...this.scheduleForm.run_hms, "12:00"];
    },
    removeSlot(idx) {
      if (this.scheduleForm.run_hms.length <= 1) return;
      const next = this.scheduleForm.run_hms.filter((_, i) => i !== idx);
      this.scheduleForm.run_hms = next.length ? next : ["05:30"];
      this.scheduleForm.run_hm = this.scheduleForm.run_hms[0];
    },
    selectAllShops() {
      this.scheduleForm.shop_ids = this.shopList.map((s) => s.id);
    },
    async saveConfig() {
      const runHms = normalizeRunHms(this.scheduleForm.run_hms, this.scheduleForm.run_hm);
      if (this.scheduleForm.enabled == 1) {
        if (!runHms.length) {
          this.$message.warning("请设置每天运行时间");
          return;
        }
        if (!this.scheduleForm.shop_ids?.length) {
          this.$message.warning("请选择要定时清理的店铺");
          return;
        }
      }
      if (this.scheduleForm.op_type === "delete") {
        try {
          await this.$confirm(
            "处理方式为「删除商品」，定时任务将物理删除低质商品且不可恢复，是否继续保存？",
            "提示",
            { type: "warning" }
          );
        } catch (e) {
          return;
        }
      }
      this.saving = true;
      try {
        const res = await this.$call.invoke("SqliteDB.excute", {
          action: "saveLowQualityScheduleConfig",
          data: {
            enabled: this.scheduleForm.enabled,
            run_hm: runHms[0] || "05:30",
            run_hms: runHms,
            op_type: this.scheduleForm.op_type,
            protect_days: this.scheduleForm.protect_days,
            shop_ids: this.scheduleForm.shop_ids.map(String),
          },
        });
        if (res.code == 1) {
          this.$message.success("规则已保存");
          if (res.data) {
            const savedHms = normalizeRunHms(res.data.run_hms, res.data.run_hm);
            this.scheduleForm.run_hms = savedHms;
            this.scheduleForm.run_hm = savedHms[0] || "05:30";
            this.scheduleForm.last_run_day =
              res.data.last_run_day || this.scheduleForm.last_run_day;
            this.scheduleForm.last_run_slots = Array.isArray(res.data.last_run_slots)
              ? res.data.last_run_slots
              : this.scheduleForm.last_run_slots;
            this.scheduleForm.last_run_at =
              res.data.last_run_at || this.scheduleForm.last_run_at;
            this.scheduleForm.last_run_msg =
              res.data.last_run_msg || this.scheduleForm.last_run_msg;
          }
          this.$emit("saved", this.summary);
          this.visible = false;
        } else {
          this.$message.error(res.message || "保存失败");
        }
      } catch (e) {
        this.$message.error(e?.message || "保存失败");
      }
      this.saving = false;
    },
  },
};
</script>

<style scoped>
.schedule-hint {
  margin: 0 0 14px;
  font-size: 12px;
  color: #909399;
  line-height: 1.6;
}
.form-tip {
  margin-left: 10px;
  font-size: 12px;
  color: #909399;
}
.time-slots {
  width: 100%;
}
.time-slot-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}
.slot-remove {
  margin-left: 8px;
  color: #f56c6c;
}
.slot-tip {
  display: block;
  margin: 4px 0 0;
  margin-left: 0;
}
.shop-actions {
  margin-top: 2px;
}
.protect-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  color: #606266;
}
.protect-input {
  width: 110px;
}
.protect-tip {
  display: block;
  margin: 6px 0 0;
  margin-left: 0;
  color: #8aa0b8;
}
.last-run-box {
  line-height: 1.5;
}
.last-run {
  font-size: 13px;
  color: #606266;
}
.last-run-msg {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
  word-break: break-all;
  white-space: pre-wrap;
}
.last-run-msg.is-warning {
  color: #e6a23c;
}
.last-run-msg.is-error {
  color: #f56c6c;
}
</style>
